import { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";

import AppLayout from "../layouts/AppLayout";
import ErrorBoundary from "../components/ErrorBoundary/ErrorBoundary";
import ProtectedRoute from "./ProtectedRoute";
import { organizationType, permissions } from "../utils/helpers/permissions";
import { Spinner } from "../components/UiKits/spinner/spinner";

// Lazy load components
const ChangePassword = lazy(() => import("../pages/change-password/change-password"));
const Categorized = lazy(() => import("../pages/flightOperations/categorized"));
const Home = lazy(() => import("../pages/home/home"));
const Login = lazy(() => import("../pages/login/login"));
const ResetPassword = lazy(() => import("../pages/reset-password/reset-password"));
const SendPasswordLink = lazy(() => import("../pages/send-password-link/send-password-link"));
const AccountActivation = lazy(() => import("../pages/authetications/AccountActivation/AccountActivation"));
const InvoiceAndBilling = lazy(() => import("../pages/invoice-and-billing/InvoiceAndBilling"));
const InvoiceAndBillingDetails = lazy(() => import("../pages/invoice-and-billing/InvoiceAndBillingDetails"));
const CategorizedDetails = lazy(() => import("../pages/flightOperations/categorized-details"));
const Disputes = lazy(() => import("../pages/disputes/Disputes"));
const DisputeDetails = lazy(() => import("../pages/disputes/DisputeDetails"));
const OperatorRegistry = lazy(() => import("../pages/operator-registry/OperatorRegistry"));
const OperatorRegistryDetails = lazy(() => import("../pages/operator-registry/OperatorRegistryDetails"));
const Requests = lazy(() => import("../pages/requests/Requests"));
const RequestDetails = lazy(() => import("../pages/requests/RequestDetails"));
const Uncategorized = lazy(() => import("../pages/flightOperations/uncategorized"));
const UnCategorizedDetails = lazy(() => import("../pages/flightOperations/uncategorized-details"));
const AircraftRegistry = lazy(() => import("../pages/aircraft-registry/AircraftRegistry"));
const AircraftRegistryDetail = lazy(() => import("../pages/aircraft-registry/AircraftRegistryDetail"));
const Notifications = lazy(() => import("../pages/notifications/Notifications"));
const Settings = lazy(() => import("../pages/settings/settings"));
const ManifestTracker = lazy(() => import("../pages/reports/manifest-tracker/manifest-tracker"));
const FlightTracker = lazy(() => import("../pages/reports/flight-tracker/flight-tracker"));
const PncfTracker = lazy(() => import("../pages/reports/pncf-tracker/pncf-tracker"));
const Analytics = lazy(() => import("../pages/reports/analytics/analytics"));
const CreateInvoice = lazy(() => import("../pages/invoice-and-billing/create-invoice/CreateInvoice"));
const AuditLog = lazy(() => import("../pages/audit-log/AuditLog"));
const Faq = lazy(() => import("../pages/Faq/Faq"));

const indexRoutes = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "/",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <Login />
          </Suspense>
        ),
      },
      {
        path: "/forgot-password",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ResetPassword />
          </Suspense>
        ),
      },
      {
        path: "/send-password-link/:email",
        element: <SendPasswordLink />,
        // loader: redirectIfUser,
      },
      {
        path: "/reset-password",
        // reset-password
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ChangePassword />
          </Suspense>
        ),
      },
      {
        path: "/account/confirm",
        // reset-password
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            < AccountActivation />
          </Suspense>
        ),
      },
      {
        path: "/home",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[]}>
              <Home />{" "}
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/flight-operations/",
        children: [
          {
            path: "categorized",
            element: (
              <Suspense
                fallback={
                  <div className="h-screen flex items-center justify-center">
                    {" "}
                    <Spinner />.
                  </div>
                }
              >
                <ProtectedRoute requiredPermissions={[permissions.FLIGHTOPS]}>
                  {" "}
                  <Categorized />{" "}
                </ProtectedRoute>
              </Suspense>
            ),
          },
          {
            path: "categorized/:id",
            element: (
              <Suspense
                fallback={
                  <div className="h-screen flex items-center justify-center">
                    {" "}
                    <Spinner />.
                  </div>
                }
              >
                <ProtectedRoute requiredPermissions={[permissions.FLIGHTOPS]}>
                  {" "}
                  <CategorizedDetails />{" "}
                </ProtectedRoute>
              </Suspense>
            ),
          },
          {
            path: "uncategorized",
            element: (
              <Suspense
                fallback={
                  <div className="h-screen flex items-center justify-center">
                    {" "}
                    <Spinner />.
                  </div>
                }
              >
                <ProtectedRoute requiredPermissions={[permissions.FLIGHTOPS]} allowedOrgTypes={[organizationType.NCAA]}>
                  <Uncategorized />{" "}
                </ProtectedRoute>
              </Suspense>
            ),
          },
          {
            path: "uncategorized/:id",
            element: (
              <Suspense
                fallback={
                  <div className="h-screen flex items-center justify-center">
                    {" "}
                    <Spinner />.
                  </div>
                }
              >
                <ProtectedRoute requiredPermissions={[permissions.FLIGHTOPS]} allowedOrgTypes={[organizationType.NCAA]}>
                  {" "}
                  <UnCategorizedDetails />{" "}
                </ProtectedRoute>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "/invoice-and-billing",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.BILLING]}>
              {" "}
              <InvoiceAndBilling />{" "}
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/invoice-and-billing/:invoiceNumber",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.BILLING]}>
              <InvoiceAndBillingDetails />,
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/create-invoice",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.BILLING]}>
              <CreateInvoice />,
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/aircraft-registry",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.OPERATIONS]}>
              <AircraftRegistry />,
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/aircraft-registry/:id",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.OPERATIONS]}>
              <AircraftRegistryDetail />,
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/operator-registry",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.OPERATIONS]}>
              <OperatorRegistry />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/operator-registry/:id",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.OPERATIONS]}>
              <OperatorRegistryDetails />,
            </ProtectedRoute>
          </Suspense>
        ),
      },

      {
        path: "/disputes",

        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.FLIGHTOPS]}>
              <Disputes />,
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/dispute/:item",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.FLIGHTOPS]}>
              <DisputeDetails />,
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/requests",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.FLIGHTOPS_APPROVE || permissions.BILLING_APPROVE]}>
              <Requests />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/request/:id",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.FLIGHTOPS_APPROVE || permissions.BILLING_APPROVE]}>
              <RequestDetails />
            </ProtectedRoute>
          </Suspense>
        ),
      },

      {
        path: "/notifications",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[]}>
              <Notifications />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/settings",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.OPERATIONS]}>
              <Settings />
            </ProtectedRoute>
          </Suspense>
        ),
      },

      {
        path: "/reporting/",
        children: [
          {
            path: "analytics",
            element: (
              <Suspense
                fallback={
                  <div className="h-screen flex items-center justify-center">
                    {" "}
                    <Spinner />.
                  </div>
                }
              >
                <ProtectedRoute requiredPermissions={[permissions.REPORT_ANALYTICS]}>
                  <Analytics />
                </ProtectedRoute>
              </Suspense>
            ),
          },
          {
            path: "manifest-tracker",
            element: (
              <Suspense
                fallback={
                  <div className="h-screen flex items-center justify-center">
                    {" "}
                    <Spinner />.
                  </div>
                }
              >
                <ProtectedRoute requiredPermissions={[permissions.REPORT_MANIFEST_TRACKER]}>
                  {" "}
                  <ManifestTracker />{" "}
                </ProtectedRoute>
              </Suspense>
            ),
          },
          {
            path: "pncf-tracker",
            element: (
              <Suspense
                fallback={
                  <div className="h-screen flex items-center justify-center">
                    {" "}
                    <Spinner />.
                  </div>
                }
              >
                <ProtectedRoute requiredPermissions={[permissions.REPORT_PNCF_TRACKER]}>
                  <PncfTracker />{" "}
                </ProtectedRoute>
              </Suspense>
            ),
          },
          {
            path: "flight-tracker",
            element: (
              <Suspense
                fallback={
                  <div className="h-screen flex items-center justify-center">
                    {" "}
                    <Spinner />.
                  </div>
                }
              >
                <ProtectedRoute requiredPermissions={[permissions.REPORT_FLIGHT_TRACKER]}>
                  {" "}
                  <FlightTracker />{" "}
                </ProtectedRoute>
              </Suspense>
            ),
          },
        ],
      },
      {
        path: "/audit-log",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />.
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[permissions.AUDIT_LOG]}>
              <AuditLog />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: "/faq",
        element: (
          <Suspense
            fallback={
              <div className="h-screen flex items-center justify-center">
                {" "}
                <Spinner />
              </div>
            }
          >
            <ProtectedRoute requiredPermissions={[]}>
              <Faq />
            </ProtectedRoute>
          </Suspense>
        ),
      },
    ],
  },
]);

export default indexRoutes;
